/**
 * Job Detail Page component
 * Displays comprehensive job posting information with loading and error states
 */
import React from 'react'
import { useParams } from 'react-router-dom'
import { useJobPosting } from '@/hooks/useJobPosting'
import { JobDetail } from '@/components/job/JobDetail'
import { JobSkeleton } from '@/components/job/JobSkeleton'
import { ApplicationModal } from '@/components/application/ApplicationModal'
import { useTranslation } from '@/hooks/useTranslation'

export const JobDetailPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>()
  const { data: jobPosting, isLoading, isError, error } = useJobPosting(jobId || '')
  const { t } = useTranslation()

  if (!jobId) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-ds-text mb-2">
            {t('job.error.invalidPosting')}
          </h1>
          <p className="text-ds-text-secondary">{t('job.error.noJobId')}</p>
        </div>
      </div>
    )
  }

  // Loading state
  if (isLoading) {
    return <JobSkeleton />
  }

  // Error state
  if (isError) {
    const errorMessage =
      error instanceof Error ? error.message : 'An unexpected error occurred'

    // Check if it's a 404 error
    const is404 =
      errorMessage.includes('404') ||
      errorMessage.toLowerCase().includes('not found')

    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-ds-text mb-2">
            {is404 ? t('job.error.notFound') : t('job.error.unableToLoad')}
          </h1>
          <p className="text-ds-text-secondary mb-4">
            {is404
              ? t('job.error.notFoundDescription')
              : t('job.error.unableToLoadDescription')}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-surface rounded-md hover:bg-primary-hover transition-colors"
          >
            {t('common.actions.tryAgain')}
          </button>
        </div>
      </div>
    )
  }

  // Not found state (null data)
  if (!jobPosting) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-ds-text mb-2">
            {t('job.error.notFound')}
          </h1>
          <p className="text-ds-text-secondary">
            {t('job.error.notFoundDescription')}
          </p>
        </div>
      </div>
    )
  }

  // Success state
  return (
    <>
      <JobDetail jobPosting={jobPosting} />
      <ApplicationModal />
    </>
  )
}
